import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { CalendarPlus, ShieldCheck, Sparkles, StickyNote } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import api, { errMsg } from "@/lib/api";
import { DateTimePicker } from "@/components/DateTimePicker";

export const ATS_STAGES = ["applied", "screening", "shortlisted", "assessment", "interview",
  "final_interview", "selected", "offer", "bgv", "joined", "rejected", "hold"];

export function KanbanBoard({ applications, onRefresh, showJob = true }) {
  const [interviewFor, setInterviewFor] = useState(null);
  const [scheduleMode, setScheduleMode] = useState("fixed");
  const [slots, setSlots] = useState(["", ""]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [panel, setPanel] = useState([]);
  const [noteFor, setNoteFor] = useState(null);
  const [noteText, setNoteText] = useState("");
  const [iv, setIv] = useState({ scheduled_at: "", mode: "video", round: "Round 1", interviewer: "", meeting_link: "" });
  const [scores, setScores] = useState({});
  const [busyScore, setBusyScore] = useState(null);
  const [bgvFor, setBgvFor] = useState(null);
  const [bgvChecks, setBgvChecks] = useState(["identity", "address", "education", "employment", "reference"]);
  const [bgvBusy, setBgvBusy] = useState(false);
  const boardRef = useRef(null);

  const startBgv = async () => {
    setBgvBusy(true);
    try {
      await api.post(`/employer/applications/${bgvFor.id}/bgv`, { checks: bgvChecks });
      toast.success("BGV initiated — candidate consent requested");
      setBgvFor(null);
      onRefresh();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBgvBusy(false);
    }
  };

  useEffect(() => {
    boardRef.current?.scrollTo({ left: 0 });
    const t = setTimeout(() => boardRef.current?.scrollTo({ left: 0 }), 150);
    return () => clearTimeout(t);
  }, [applications]);

  const move = async (appId, stage) => {
    try {
      await api.patch(`/applications/${appId}/stage`, { stage });
      toast.success(`Moved to ${stage.replace(/_/g, " ")}`);
      onRefresh();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const score = async (appId) => {
    setBusyScore(appId);
    try {
      const { data } = await api.post("/ai/match-score", { application_id: appId });
      setScores((s) => ({ ...s, [appId]: data }));
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusyScore(null);
    }
  };

  const scheduleInterview = async () => {
    try {
      if (scheduleMode === "slots") {
        const clean = slots.filter(Boolean);
        if (clean.length === 0) {
          toast.error("Add at least one slot");
          return;
        }
        await api.post(`/applications/${interviewFor.id}/interviews/slots`, {
          slots: clean, mode: iv.mode, round: iv.round, panel,
        });
        toast.success("Slots proposed — candidate will pick one");
      } else {
        await api.post(`/applications/${interviewFor.id}/interviews`, { ...iv, panel });
        toast.success("Interview scheduled & candidate notified");
      }
      setInterviewFor(null);
      onRefresh();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const addNote = async () => {
    try {
      await api.post(`/applications/${noteFor.id}/notes`, { text: noteText });
      toast.success("Note added");
      setNoteFor(null);
      setNoteText("");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <div className="overflow-x-auto pb-4" ref={boardRef}>
      <div className="flex gap-4 min-w-max" data-testid="kanban-board">
        {ATS_STAGES.map((stage) => {
          const apps = applications.filter((a) => a.stage === stage);
          return (
            <div
              key={stage}
              className="w-72 shrink-0 bg-[#F1F5F9] rounded-xl p-3 border border-border/50"
              data-testid={`kanban-col-${stage}`}
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => {
                const id = e.dataTransfer.getData("appId");
                if (id) move(id, stage);
              }}
            >
              <div className="flex items-center justify-between px-1.5 mb-3">
                <p className="text-xs font-bold uppercase tracking-wide text-slate-600 capitalize">
                  {stage.replace(/_/g, " ")}
                </p>
                <span className="text-xs bg-white rounded-full px-2 py-0.5 font-semibold border border-border/60">{apps.length}</span>
              </div>
              <div className="space-y-2.5 min-h-[60px]">
                {apps.map((a) => (
                  <div
                    key={a.id}
                    draggable
                    onDragStart={(e) => e.dataTransfer.setData("appId", a.id)}
                    className="bg-white rounded-lg border border-border/60 p-3.5 cursor-grab active:cursor-grabbing card-hover"
                    data-testid={`app-card-${a.id}`}
                  >
                    <p className="font-heading font-semibold text-sm text-[#0B1B3D]">{a.candidate_name}</p>
                    {showJob && <p className="text-[11px] text-muted-foreground mt-0.5">{a.job_title}</p>}
                    {a.candidate_headline && <p className="text-[11px] text-muted-foreground">{a.candidate_headline}</p>}
                    {a.source === "partner" && (
                      <span className="text-[10px] bg-[#F5A623]/15 text-[#8a5a00] rounded-full px-2 py-0.5 font-semibold mt-1.5 inline-block">
                        Partner submission
                      </span>
                    )}
                    {a.stage === "offer" && a.offer_response && (
                      <span className={`text-[10px] rounded-full px-2 py-0.5 font-semibold mt-1.5 inline-block ${
                        a.offer_response === "accepted" ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"
                      }`} data-testid={`offer-response-${a.id}`}>
                        Offer {a.offer_response}
                      </span>
                    )}
                    {scores[a.id] && (
                      <div className="mt-2 text-xs bg-[#2563EB]/5 border border-[#2563EB]/15 rounded-lg p-2" data-testid={`match-score-${a.id}`}>
                        <p className="font-bold text-[#2563EB]">{scores[a.id].score}% match</p>
                        <p className="text-muted-foreground mt-0.5 leading-snug">{scores[a.id].summary}</p>
                      </div>
                    )}
                    <div className="flex gap-1.5 mt-3">
                      <Button size="sm" variant="ghost" className="h-7 px-2 text-xs" onClick={() => score(a.id)} disabled={busyScore === a.id} data-testid={`ai-score-${a.id}`}>
                        <Sparkles className="w-3 h-3 mr-1" /> {busyScore === a.id ? "…" : "AI"}
                      </Button>
                      <Button size="sm" variant="ghost" className="h-7 px-2 text-xs" onClick={() => { setInterviewFor(a); setScheduleMode("fixed"); setSlots(["", ""]); setPanel([]); api.get("/employer/team").then(({ data }) => setTeamMembers(data.members)).catch(() => {}); }} data-testid={`schedule-${a.id}`}>
                        <CalendarPlus className="w-3 h-3 mr-1" /> Interview
                      </Button>
                      <Button size="sm" variant="ghost" className="h-7 px-2 text-xs" onClick={() => setNoteFor(a)} data-testid={`note-${a.id}`}>
                        <StickyNote className="w-3 h-3" />
                      </Button>
                      {["selected", "offer", "bgv", "final_interview"].includes(a.stage) && (
                        <Button size="sm" variant="ghost" className="h-7 px-2 text-xs text-cyan-700" onClick={() => setBgvFor(a)} data-testid={`bgv-${a.id}`}>
                          <ShieldCheck className="w-3 h-3 mr-1" /> BGV
                        </Button>
                      )}
                    </div>
                    <Select onValueChange={(v) => move(a.id, v)} value={a.stage}>
                      <SelectTrigger className="h-7 text-xs mt-2" data-testid={`move-${a.id}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {ATS_STAGES.map((s) => (
                          <SelectItem key={s} value={s} className="capitalize text-xs">{s.replace(/_/g, " ")}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      <Dialog open={!!interviewFor} onOpenChange={() => setInterviewFor(null)}>
        <DialogContent aria-describedby={undefined} data-testid="interview-dialog">
          <DialogHeader>
            <DialogTitle>Schedule interview — {interviewFor?.candidate_name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="flex gap-2">
              <button type="button" onClick={() => setScheduleMode("fixed")} data-testid="schedule-fixed-tab"
                className={`text-xs font-semibold rounded-full px-4 py-2 border ${scheduleMode === "fixed" ? "bg-[#0B1B3D] text-white border-[#0B1B3D]" : "border-border"}`}>
                Fixed time
              </button>
              <button type="button" onClick={() => setScheduleMode("slots")} data-testid="schedule-slots-tab"
                className={`text-xs font-semibold rounded-full px-4 py-2 border ${scheduleMode === "slots" ? "bg-[#0B1B3D] text-white border-[#0B1B3D]" : "border-border"}`}>
                Propose slots (candidate picks)
              </button>
            </div>
            {scheduleMode === "fixed" ? (
              <div>
                <Label>Date & time</Label>
                <div className="mt-1.5">
                  <DateTimePicker value={iv.scheduled_at} onChange={(v) => setIv({ ...iv, scheduled_at: v })} testId="interview-time-input" />
                </div>
              </div>
            ) : (
              <div className="space-y-2">
                {slots.map((s, i) => (
                  <DateTimePicker key={i} value={s} testId={`slot-input-${i}`}
                    onChange={(v) => setSlots(slots.map((x, xi) => xi === i ? v : x))} />
                ))}
                {slots.length < 5 && (
                  <Button type="button" variant="ghost" size="sm" onClick={() => setSlots([...slots, ""])} data-testid="add-slot-btn">
                    + Add another slot
                  </Button>
                )}
              </div>
            )}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Mode</Label>
                <Select value={iv.mode} onValueChange={(v) => setIv({ ...iv, mode: v })}>
                  <SelectTrigger className="mt-1.5" data-testid="interview-mode-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="phone">Phone</SelectItem>
                    <SelectItem value="video">Video</SelectItem>
                    <SelectItem value="in_person">In-person</SelectItem>
                    <SelectItem value="panel">Panel</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Round</Label>
                <Input value={iv.round} onChange={(e) => setIv({ ...iv, round: e.target.value })} className="mt-1.5" data-testid="interview-round-input" />
              </div>
            </div>
            <div>
              <Label>Meeting link (optional — auto-generated for video)</Label>
              <Input value={iv.meeting_link} onChange={(e) => setIv({ ...iv, meeting_link: e.target.value })} placeholder="Google Meet / Zoom / Teams link" className="mt-1.5" data-testid="interview-link-input" />
            </div>
            {teamMembers.length > 0 && (
              <div>
                <Label>Panel interviewers (optional)</Label>
                <div className="flex flex-wrap gap-2 mt-2">
                  {teamMembers.map((m) => (
                    <button
                      key={m.id}
                      type="button"
                      onClick={() => setPanel((p) => p.includes(m.email) ? p.filter((x) => x !== m.email) : [...p, m.email])}
                      data-testid={`panel-${m.id}`}
                      className={`text-xs font-semibold rounded-full px-3 py-1.5 border transition-colors ${
                        panel.includes(m.email) ? "bg-[#0B1B3D] text-white border-[#0B1B3D]" : "bg-white border-border"
                      }`}
                    >
                      {m.name}
                    </button>
                  ))}
                </div>
              </div>
            )}
            <Button onClick={scheduleInterview} disabled={scheduleMode === "fixed" && !iv.scheduled_at} className="w-full rounded-full bg-[#0B1B3D]" data-testid="interview-submit-btn">
              {scheduleMode === "slots" ? "Propose Slots to Candidate" : "Schedule & Notify Candidate"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!bgvFor} onOpenChange={() => setBgvFor(null)}>
        <DialogContent aria-describedby={undefined} data-testid="bgv-dialog">
          <DialogHeader>
            <DialogTitle>Initiate BGV — {bgvFor?.candidate_name}</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            The candidate will be asked for consent before any verification begins.
          </p>
          <div className="flex flex-wrap gap-2">
            {["identity", "address", "education", "employment", "reference"].map((c) => (
              <button
                key={c}
                type="button"
                onClick={() => setBgvChecks((cs) => cs.includes(c) ? cs.filter((x) => x !== c) : [...cs, c])}
                data-testid={`bgv-check-${c}`}
                className={`text-xs font-semibold rounded-full px-4 py-2 border capitalize transition-colors ${
                  bgvChecks.includes(c) ? "bg-[#0B1B3D] text-white border-[#0B1B3D]" : "bg-white border-border"
                }`}
              >
                {c}
              </button>
            ))}
          </div>
          <Button onClick={startBgv} disabled={bgvBusy || bgvChecks.length === 0} className="w-full rounded-full bg-[#0B1B3D]" data-testid="bgv-start-btn">
            {bgvBusy ? "Initiating…" : "Initiate Verification"}
          </Button>
        </DialogContent>
      </Dialog>

      <Dialog open={!!noteFor} onOpenChange={() => setNoteFor(null)}>
        <DialogContent aria-describedby={undefined} data-testid="note-dialog">
          <DialogHeader>
            <DialogTitle>Note — {noteFor?.candidate_name}</DialogTitle>
          </DialogHeader>
          <Textarea rows={4} value={noteText} onChange={(e) => setNoteText(e.target.value)} placeholder="Screening note, feedback…" data-testid="note-text-input" />
          <Button onClick={addNote} disabled={!noteText.trim()} className="rounded-full bg-[#0B1B3D]" data-testid="note-submit-btn">
            Add Note
          </Button>
          {noteFor?.notes?.length > 0 && (
            <div className="space-y-2 mt-2 max-h-40 overflow-y-auto">
              {noteFor.notes.map((n) => (
                <div key={n.id} className="text-xs bg-[#F8FAFC] border border-border/60 rounded-lg p-2.5">
                  <p>{n.text}</p>
                  <p className="text-muted-foreground mt-1">— {n.by}</p>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
